import { allSettled, formatPagingParams, handleError } from '../common/services/helpers';
import {
    formatDomain,
    formatDomainListResults,
    formatDomainResults,
    formatDomainScan,
    formatDomainScans,
    formatDomainSummaryResults,
} from './format';

import API from '/b2b/common/helpers/API';
import { API_NAME } from '#config/runtimeconfig';
import { SCAN_SOURCES } from './constants';

// Map frontend column field names to backend sortBy field names
const SORT_FIELD_MAP = {
    scans: 'failedChecksCount',
    latestScan: 'latestScanDate',
    scheduledScan: 'nextScheduledScan',
};

const LIST_SORT_FIELD_MAP = {
    scans: 'failedChecksCount',
    failedChecksCount: 'failedChecksCount',
    latestScan: 'latestScan',
    scheduledScan: 'scheduledScan',
    scanStatus: 'scanStatus',
    domain: 'domain',
};

export const fetchDomains = params => {
    const options = {
        queryStringParameters: {
            ...formatPagingParams(params),
        },
    };
    return API.get(API_NAME, '/domains', options).catch(handleError).then(formatDomainResults);
};

export const fetchDomain = domain => {
    const options = {};
    return API.get(API_NAME, `/domains/${domain}`, options).catch(handleError).then(formatDomain);
};

export const updateDomain = body => {
    const { domain } = body;
    const options = {
        body,
    };
    return API.patch(API_NAME, `/domains/${domain}`, options).then(formatDomain).catch(handleError);
};

export const deleteDomain = body => {
    const { domain } = body;
    const options = {};
    return API.del(API_NAME, `/domains/${domain}`, options).catch(handleError);
};

export const deleteDomains = domains => {
    return allSettled(domains.map(domain => deleteDomain({ domain })));
};

export const createDomain = body => {
    const options = {
        body,
    };
    return API.post(API_NAME, `/domains`, options).catch(handleError);
};

export const checkDomainReachable = domain => {
    return API.post(API_NAME, `/website-scan/api/v1/scan/check-domain`, {
        body: { domain },
    }).catch(handleError);
};

export const createDomainScan = (domain, sitemap = {}) => {
    const options = {
        body: {
            domain,
            source: SCAN_SOURCES.MANUAL,
            ...(Object.keys(sitemap).length
                ? {
                      options: {
                          sitemap,
                      },
                  }
                : {}),
        },
    };
    return API.post(API_NAME, `/website-scan/api/v1/scan`, options).catch(handleError);
};

export const fetchDomainScans = (params = {}) => {
    const options = {
        queryStringParameters: {
            ...params,
        },
    };
    return API.get(API_NAME, `/website-scan/api/v1/scan`, options)
        .catch(handleError)
        .then(response => formatDomainScans(params, response));
};

export const fetchDomainScan = id => {
    return API.get(API_NAME, `/website-scan/api/v1/scan/${id}`)
        .catch(handleError)
        .then(formatDomainScan);
};

export const fetchDomainSummaries = (params = {}) => {
    const {
        pageSize = 25,
        limit = pageSize,
        page = 1,
        offset,
        query,
        sort,
        sortDirection,
        status,
        filters,
    } = params;
    const { status: resultStatus } = filters || {};

    const queryStringParameters = {};

    if (query) {
        queryStringParameters.search = query;
    }
    if (status) {
        queryStringParameters.status = status;
    }
    if (resultStatus) {
        queryStringParameters.resultStatus = resultStatus;
    }
    if (limit) {
        queryStringParameters.pageSize = limit;
    }
    if (offset && limit) {
        queryStringParameters.page = Math.floor(offset / limit) + 1;
    } else {
        queryStringParameters.page = page;
    }
    if (sort) {
        queryStringParameters.sortBy = SORT_FIELD_MAP[sort] ?? sort;
        queryStringParameters.sortOrder = sortDirection || 'asc';
    }

    const options = { queryStringParameters };
    return API.get(API_NAME, `/website-scan/api/v1/scan/compliance-check/sites/summary`, options)
        .catch(handleError)
        .then(formatDomainSummaryResults);
};

export const fetchDomainList = (params = {}) => {
    const {
        pageSize = 25,
        limit = pageSize,
        page = 1,
        offset,
        query,
        sort,
        sortDirection,
        status,
        filters,
        includeTotals,
        excludeChecks,
    } = params;
    const { status: resultStatus } = filters || {};

    const queryStringParameters = {};

    if (query) {
        queryStringParameters.search = query;
    }
    if (status) {
        queryStringParameters.scanStatus = status;
    }
    if (resultStatus) {
        queryStringParameters.resultStatus = resultStatus;
    }
    if (limit) {
        queryStringParameters.pageSize = limit;
    }
    if (offset && limit) {
        queryStringParameters.page = Math.floor(offset / limit) + 1;
    } else {
        queryStringParameters.page = page;
    }
    if (sort) {
        queryStringParameters.sortBy = LIST_SORT_FIELD_MAP[sort] ?? sort;
        queryStringParameters.sortOrder = sortDirection || 'asc';
    }
    if (includeTotals === false) {
        queryStringParameters.includeTotals = false;
    }
    if (excludeChecks) {
        queryStringParameters.excludeChecks = excludeChecks;
    }

    const options = { queryStringParameters };
    return API.get(API_NAME, `/website-scan/api/v1/scan/compliance-check/sites/list`, options)
        .catch(handleError)
        .then(formatDomainListResults);
};

export const fetchCmpConfigByDomains = async domains => {
    // Ensure domains is an array
    const domainsArray = Array.isArray(domains) ? domains : [domains];

    // Manually construct query string for array
    const queryString = domainsArray
        .map(domain => `domains[]=${encodeURIComponent(domain)}`)
        .join('&');

    try {
        const response = await API.get(
            API_NAME,
            `/consentmanager/configs?sortField=updated&sortDir=desc&pageSize=1&${queryString}`
        );
        const latestUpdatedConfig = response?.data ? response.data[0] : null;

        return latestUpdatedConfig;
    } catch (error) {
        handleError(error);
    }
};
